import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Icon } from '../components/ui/Icon';

const SECTIONS = [
  {
    id: 'eligibility',
    title: '1. Eligibility & Account Registration',
    body: [
      'AB8 Beauty ("AB8 Beauty", "we", "us") sells topical skincare and cosmetic products. By creating an account and placing an order, you represent and warrant that you are at least 18 years of age (or the age of majority in your jurisdiction) and able to enter into a binding agreement.',
      'You agree to provide accurate information during registration and to keep your account credentials secure. AB8 Beauty reserves the right to suspend or terminate accounts that provide false information or misuse the service.',
    ],
  },
  {
    id: 'product-use',
    title: '2. Product Use & Safety',
    body: [
      'AB8 Beauty products are cosmetics intended for external use on the skin and hair. They are not drugs and are not intended to diagnose, treat, cure, or prevent any disease or condition. These statements have not been evaluated by the U.S. Food and Drug Administration.',
      'We recommend performing a patch test before first use and discontinuing use if irritation occurs. If you are pregnant, nursing, under a doctor\'s care, or have a known allergy (such as a bee allergy, relevant to our bee venom products), please consult a healthcare professional before use. You agree to use our products only as directed and for their intended cosmetic purpose.',
    ],
  },
  {
    id: 'orders',
    title: '3. Orders, Pricing & Payment',
    body: [
      'All prices are listed in United States dollars (USD) and are exclusive of applicable taxes. AB8 Beauty reserves the right to change prices at any time without notice. Your order constitutes an offer to purchase; acceptance occurs when AB8 Beauty sends an order confirmation email.',
      'Payment is processed securely via Stripe. By placing an order you authorise the charge to your selected payment method. Referral Cash (your accrued referral balance) may be applied at checkout against eligible item subtotals before charging your card. Referral Cash cannot be applied to shipping charges; shipping is payable in full on every order.',
    ],
  },
  {
    id: 'shipping',
    title: '4. Shipping & Delivery',
    body: [
      'AB8 Beauty currently ships within the United States. Shipping is free on orders over $50; otherwise a flat shipping rate is applied at checkout. Estimated delivery times are provided at checkout and are not guaranteed. Risk of loss passes to you upon handover to the carrier. If your order is lost or damaged in transit, contact support@ab8beauty.com within 7 calendar days of the expected delivery date.',
    ],
  },
  {
    id: 'returns',
    title: '5. Returns & Refunds',
    body: [
      'For hygiene and safety reasons, opened or used skincare products cannot be returned. Unopened products in their original packaging may be returned within 30 days of delivery for a refund of the item price. If you receive a defective or incorrect product, contact us within 5 business days of receipt with your order number and photographs of the product and packaging, and we\'ll make it right. Approved refunds are credited to your original payment method within 10 business days.',
    ],
  },
  {
    id: 'affiliate',
    title: '6. Referral Program',
    body: [
      'The AB8 Beauty referral program is available to account holders. You earn referral cash on qualifying orders placed by customers you have directly or indirectly referred, credited to your account. Reward rates and tier structures are as published on the referral page at the time of a referred purchase and may change with 30 days\' notice.',
      'Referral Cash has no independent cash value, is non-transferable, and may only be redeemed against future AB8 Beauty purchases. It is applied against eligible item subtotals at checkout and does not cover shipping or applicable taxes. Accounts found to be abusing the referral system — including self-referrals or fraudulent accounts — will be terminated and forfeit all accrued Referral Cash.',
      'When sharing our products, you must comply with all applicable advertising and disclosure laws, including the FTC\'s endorsement guidelines. You must clearly disclose your material connection to AB8 Beauty in any content promoting our products, and you must not make therapeutic, medical, or disease-treatment claims about any AB8 Beauty product.',
    ],
  },
  {
    id: 'ip',
    title: '7. Intellectual Property',
    body: [
      'All content on the AB8 Beauty website — including text, graphics, logos, and product descriptions — is the property of AB8 Beauty or its content suppliers and is protected by applicable copyright and trademark law. You may not reproduce, distribute, or create derivative works without written permission.',
    ],
  },
  {
    id: 'liability',
    title: '8. Limitation of Liability',
    body: [
      'TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, AB8 BEAUTY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF YOUR USE OF OUR PRODUCTS OR SERVICES. OUR TOTAL LIABILITY FOR ANY CLAIM SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE ORDER GIVING RISE TO THAT CLAIM.',
      'Beyond their intended cosmetic use, products are provided "as-is." To the extent permitted by law, AB8 Beauty makes no representations or warranties — express or implied — regarding the fitness of any product for any particular purpose. Individual results may vary.',
    ],
  },
  {
    id: 'governing-law',
    title: '9. Governing Law & Disputes',
    body: [
      'These Terms are governed by the laws of the State of California, United States, without regard to conflict-of-law principles. Any disputes shall be resolved in the state or federal courts located in California.',
    ],
  },
  {
    id: 'changes',
    title: '10. Changes to These Terms',
    body: [
      'AB8 Beauty may update these Terms at any time. Material changes will be communicated by email and/or a notice on the website. Continued use of the site after the effective date constitutes acceptance of the revised Terms.',
      'Last updated: January 2025.',
    ],
  },
];

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      <section className="bg-tea-700 text-white py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-tea-300 text-sm font-semibold uppercase tracking-widest mb-4">Legal</p>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">Terms of Service</h1>
          <p className="mt-3 text-tea-200">Last updated January 2025 · AB8 Beauty · United States</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 flex gap-12">
        {/* Sidebar TOC — hidden on mobile */}
        <aside className="hidden lg:block w-56 shrink-0">
          <div className="sticky top-24">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Contents</p>
            <nav className="flex flex-col gap-1">
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="text-sm text-gray-500 hover:text-tea-700 transition-colors py-1 leading-tight"
                >
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-10 text-amber-900 text-sm leading-relaxed">
            <strong><Icon name="leaf" className="inline w-4 h-4 align-[-2px] mr-1 text-tea-600" />Good to know:</strong> AB8 Beauty products are topical skincare and cosmetics for external use. They are not intended to diagnose, treat, cure, or prevent any disease. We recommend a patch test before first use — see Section 2.
          </div>

          <div className="prose prose-gray max-w-none">
            {SECTIONS.map((s) => (
              <section key={s.id} id={s.id} className="mb-10 scroll-mt-24">
                <h2 className="text-xl font-bold text-gray-900 mb-4">{s.title}</h2>
                {s.body.map((para, i) => (
                  <p key={i} className="text-gray-600 leading-relaxed mb-4">{para}</p>
                ))}
              </section>
            ))}
          </div>

          <div className="mt-10 pt-8 border-t border-gray-200 flex flex-wrap gap-4">
            <Link to="/privacy" className="text-tea-700 font-medium hover:underline text-sm">Privacy Policy</Link>
            <Link to="/compliance" className="text-tea-700 font-medium hover:underline text-sm">Ingredients & Safety</Link>
            <a href="mailto:support@ab8beauty.com" className="text-tea-700 font-medium hover:underline text-sm">Contact Us</a>
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
}
